// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { parseAppLocale, shellCopy, type AppLocale, type ShellCopy } from "../i18n/appLocale";
import { setCurrentAppLocale } from "../i18n/currentLocale";

export const LOCALE_STORAGE_KEY = "riddle-locale";

export interface AppLocaleContextValue {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  copy: ShellCopy;
}

const AppLocaleContext = createContext<AppLocaleContextValue>({
  locale: "en",
  setLocale: () => undefined,
  copy: shellCopy("en"),
});

export function useAppLocale(): AppLocaleContextValue {
  return useContext(AppLocaleContext);
}

export function AppLocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(() => {
    const initial = parseAppLocale(localStorage.getItem(LOCALE_STORAGE_KEY));
    setCurrentAppLocale(initial);
    return initial;
  });

  function setLocale(next: AppLocale) {
    localStorage.setItem(LOCALE_STORAGE_KEY, next);
    setCurrentAppLocale(next);
    setLocaleState(next);
  }

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<AppLocaleContextValue>(
    () => ({ locale, setLocale, copy: shellCopy(locale) }),
    [locale],
  );

  return (
    <AppLocaleContext.Provider value={value}>{children}</AppLocaleContext.Provider>
  );
}
